import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Empty state — friendly, action-oriented copy with optional CTA.
 *
 * Voice convention per the GUI plan: "calm, competent, scientifically
 * literate." Never breezy, never groveling, never cute mascots. The
 * subtext should help the user understand WHY the surface is empty
 * and offer the next action when one exists.
 */
export interface EmptyStateProps {
  /** Headline — short declarative ("No runs yet."). */
  title: string;
  /** Body — explanatory ("Submit a Flow to see it here."). */
  description?: string;
  /** Optional CTA — a button, link, or any actionable element. */
  cta?: React.ReactNode;
  /** Optional decorative icon, rendered above the headline at 32px. */
  icon?: React.ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  cta,
  icon,
  className,
}: EmptyStateProps): JSX.Element {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border-subtle bg-surface-2/50 px-6 py-12 text-center",
        className
      )}
    >
      {icon && <div className="text-muted-foreground">{icon}</div>}
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
      {cta && <div className="mt-1">{cta}</div>}
    </div>
  );
}
